/**
 * Map — Osmo Supply Interactive Globe (Mapbox).
 * Mapbox GL JS is loaded on demand only when this block is present.
 */

(function () {
    'use strict';

    const SELECTOR = '[data-globe-init]';
    let mapboxPromise = null;

    function loadMapbox() {
        if (typeof window.mapboxgl !== 'undefined') {
            return Promise.resolve(window.mapboxgl);
        }

        if (!mapboxPromise) {
            mapboxPromise = import('mapbox-gl').then((mod) => {
                const mapboxgl = mod.default || mod;
                window.mapboxgl = mapboxgl;
                return mapboxgl;
            });
        }

        return mapboxPromise;
    }

    function initInteractiveGlobeMapbox(wrapper, mapboxgl) {
        const cfg = {
            mapboxToken: wrapper.getAttribute('data-mapbox-token') || '',
            mapStyle: wrapper.getAttribute('data-mapbox-style') || 'mapbox://styles/sonofesse/cmub6c774008h01r17ejp5a0e',
            center: [5, 42],
            zoom: 3.5,
            projection: 'globe',
            autoRotate: wrapper.getAttribute('data-auto-rotate') !== 'false',
            secondsPerRevolution: Number(wrapper.getAttribute('data-rotate-speed')) || 120,
            maxSpinZoom: 5,
            slowSpinZoom: 3,
            flyToDuration: 2000,
            flyToZoom: 4.8,
            globeOffsetX: -0.22,
            globeOffsetY: -0.08,
            mobile: {
                zoom: 2.5,
                flyToZoom: 3.8,
                globeOffsetX: 0,
                globeOffsetY: 0,
            },
        };

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const isSmall = () => window.matchMedia('(max-width: 991px)').matches;
        const val = (key) => (isSmall() && cfg.mobile && cfg.mobile[key] !== undefined) ? cfg.mobile[key] : cfg[key];

        if (!wrapper || wrapper.dataset.globeInit === 'initialized') return;
        if (typeof mapboxgl === 'undefined') return;
        if (!cfg.mapboxToken) return;

        mapboxgl.accessToken = cfg.mapboxToken;

        const mapEl = wrapper.querySelector('[data-globe-map]');
        if (!mapEl) return;
        if (!mapEl.id) {
            mapEl.id = 'globe-map-' + Math.random().toString(36).slice(2, 10);
        }

        const markers = readMarkers(wrapper);

        const map = new mapboxgl.Map({
            container: mapEl,
            style: cfg.mapStyle,
            center: cfg.center,
            zoom: val('zoom'),
            projection: cfg.projection,
            attributionControl: false,
            cooperativeGestures: false,
            scrollZoom: false,
            logoPosition: 'bottom-left',
        });

        map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');

        const mixHex = (from, to, amount) => {
            const parse = (hex) => {
                hex = String(hex).replace('#', '').trim();
                if (hex.length === 3) {
                    hex = hex.split('').map((c) => c + c).join('');
                }
                if (hex.length !== 6) return null;
                return [
                    parseInt(hex.slice(0, 2), 16),
                    parseInt(hex.slice(2, 4), 16),
                    parseInt(hex.slice(4, 6), 16),
                ];
            };
            const a = parse(from);
            const b = parse(to);
            if (!a || !b) return from;
            const channels = a.map((ch, i) => Math.round(ch + (b[i] - ch) * amount));
            return '#' + channels.map((n) => n.toString(16).padStart(2, '0')).join('');
        };

        const cssColor = (slug, fallback) => {
            const value = getComputedStyle(document.documentElement)
                .getPropertyValue('--wp--preset--color--' + slug)
                .trim();
            return value || fallback;
        };

        // Globe + Mapbox Standard ignore a type:background overlay on the sphere.
        // Colour/opacity live on .globe-style-overlay in CSS.
        const applyStudioBackgroundOverlay = () => {
            const container = mapEl.querySelector('.mapboxgl-canvas-container') || mapEl;
            if (container.querySelector('[data-globe-style-overlay]')) {
                return;
            }

            const overlay = document.createElement('div');
            overlay.className = 'globe-style-overlay';
            overlay.setAttribute('data-globe-style-overlay', '');
            overlay.setAttribute('aria-hidden', 'true');
            container.appendChild(overlay);
        };

        const lightenBasemapRoads = () => {
            if (typeof map.setConfigProperty !== 'function') return;

            const road = mixHex(
                cssColor('navy', '#0B2B3D'),
                cssColor('white', '#FFFFFF'),
                0.48
            );

            ['colorRoads', 'colorMotorways', 'colorTrunks'].forEach((key) => {
                try {
                    map.setConfigProperty('basemap', key, road);
                } catch (err) {
                    // Standard import not ready yet.
                }
            });
        };

        map.on('style.load', () => {
            applyStudioBackgroundOverlay();
            lightenBasemapRoads();
        });
        map.on('style.import.load', lightenBasemapRoads);

        const getPadding = (extraRight) => {
            const w = mapEl.offsetWidth;
            const h = mapEl.offsetHeight;
            const ox = val('globeOffsetX');
            const oy = val('globeOffsetY');
            return {
                top: Math.max(0, oy * h),
                bottom: Math.max(0, -oy * h),
                left: Math.max(0, ox * w),
                right: Math.max(0, -ox * w) + (extraRight || 0),
            };
        };

        let cameraLocked = false;
        let resumeSpin = () => {};

        const infoPadding = () => {
            const infoEl = wrapper.querySelector('[data-globe-info]');
            const extraRight = !isSmall() && infoEl && infoEl.offsetWidth > 0
                ? infoEl.offsetWidth + 24
                : 0;
            return getPadding(extraRight);
        };

        const flyCamera = (options) => {
            map.stop();
            cameraLocked = true;
            map.flyTo({
                duration: cfg.flyToDuration,
                essential: true,
                ...options,
            });
            window.setTimeout(() => {
                cameraLocked = false;
                resumeSpin();
            }, cfg.flyToDuration + 50);
        };

        map.on('load', () => {
            const infoEl = wrapper.querySelector('[data-globe-info]');
            const collapsed = wrapper.getAttribute('data-collapsed') === 'true';
            const infoW = !isSmall() && !collapsed && infoEl && infoEl.offsetWidth > 0
                ? infoEl.offsetWidth + 24
                : 0;
            map.setPadding(getPadding(infoW));
            applyStudioBackgroundOverlay();
            lightenBasemapRoads();

            const pins = addPins(map, markers, wrapper);

            initSlider(wrapper, markers, map, pins);
            if (cfg.autoRotate && !reducedMotion) initSpin(map);
            initPanel(wrapper, map, getPadding);
            initResize(map, wrapper, getPadding);

            wrapper.dataset.globeInit = 'initialized';
        });


        // Read markers — clone-based if cfg.markers exists, otherwise use HTML as-is
        function readMarkers(wrapperEl) {
            const list = wrapperEl.querySelector('[data-globe-list]');
            const items = Array.from(wrapperEl.querySelectorAll('[data-globe-item]'));

            if (list && items.length && cfg.markers && cfg.markers.length) {
                const tpl = items[0];
                items.forEach((el) => el.remove());

                return cfg.markers.map((m, i) => {
                    const clone = tpl.cloneNode(true);
                    clone.setAttribute('data-globe-id', m.id || 'loc-' + i);
                    clone.setAttribute('data-globe-lat', m.lat);
                    clone.setAttribute('data-globe-lng', m.lng);

                    const img = clone.querySelector('[data-globe-item-image]');
                    const city = clone.querySelector('[data-globe-item-city]');
                    const name = clone.querySelector('[data-globe-item-name]');
                    const link = clone.querySelector('[data-globe-item-link]');

                    if (img) img.src = m.image || '';
                    if (city) city.textContent = m.city || '';
                    if (name) name.textContent = m.name || '';
                    if (link) link.href = m.link || '#';

                    list.appendChild(clone);

                    return {
                        id: m.id || 'loc-' + i,
                        lat: m.lat,
                        lng: m.lng,
                        name: m.name || '',
                        city: m.city || '',
                        image: m.image || '',
                        link: m.link || '',
                        element: clone,
                    };
                });
            }

            if (items.length) {
                return items.map((el, i) => ({
                    id: el.getAttribute('data-globe-id') || 'loc-' + i,
                    lat: parseFloat(el.getAttribute('data-globe-lat')) || 0,
                    lng: parseFloat(el.getAttribute('data-globe-lng')) || 0,
                    name: txt(el, '[data-globe-item-name]') || 'Location ' + (i + 1),
                    city: txt(el, '[data-globe-item-city]') || '',
                    image: attr(el, '[data-globe-item-image]', 'src') || '',
                    link: attr(el, '[data-globe-item-link]', 'href') || '',
                    element: el,
                }));
            }

            return [];
        }


        // Map pins
        function addPins(mapInstance, markerData, wrapperEl) {
            const tpl = wrapperEl.querySelector('[data-globe-marker-template]');

            return markerData.map((data) => {
                let el;

                if (tpl) {
                    el = tpl.cloneNode(true);
                    el.removeAttribute('data-globe-marker-template');
                } else {
                    el = document.createElement('div');
                    el.className = 'globe-marker';
                    el.innerHTML = '<svg class="globe-marker__icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>';
                }

                el.setAttribute('data-globe-marker', data.id);

                const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
                    .setLngLat([data.lng, data.lat])
                    .addTo(mapInstance);

                return { marker, element: el, data };
            });
        }

        function setActive(pins, idx) {
            pins.forEach((p, i) => {
                const v = i === idx ? 'true' : 'false';
                p.element.setAttribute('data-active', v);
                if (p.data.element) p.data.element.setAttribute('data-active', v);
            });
        }


        // Slider
        function initSlider(wrapperEl, markerData, mapInstance, pins) {
            const list = wrapperEl.querySelector('[data-globe-list]');
            const prevBtn = wrapperEl.querySelector('[data-globe-prev]');
            const nextBtn = wrapperEl.querySelector('[data-globe-next]');
            const counter = wrapperEl.querySelector('[data-globe-counter]');
            if (!list || !markerData.length) return;

            let cur = -1;
            let flying = false;
            const total = markerData.length;

            const count = () => {
                if (counter && cur >= 0) counter.textContent = (cur + 1) + ' / ' + total;
            };

            const flyTo = (i) => {
                flyCamera({
                    center: [markerData[i].lng, markerData[i].lat],
                    zoom: val('flyToZoom'),
                    padding: infoPadding(),
                });
            };

            const openPanel = () => {
                wrapperEl.setAttribute('data-collapsed', 'false');
                wrapperEl.setAttribute('data-has-selection', 'true');
            };

            const go = (i) => {
                i = ((i % total) + total) % total;

                openPanel();

                cur = i;
                flying = true;

                setActive(pins, i);
                count();

                markerData[i].element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'nearest',
                });

                flyTo(i);

                setTimeout(() => { flying = false; }, cfg.flyToDuration + 200);
            };

            if (prevBtn) prevBtn.addEventListener('click', () => go(cur < 0 ? total - 1 : cur - 1));
            if (nextBtn) nextBtn.addEventListener('click', () => go(cur < 0 ? 0 : cur + 1));

            const observer = new IntersectionObserver((entries) => {
                if (flying) return;
                if (wrapperEl.getAttribute('data-collapsed') === 'true') return;
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || entry.intersectionRatio <= 0.5) return;
                    const idx = markerData.findIndex((m) => m.element === entry.target);
                    if (idx !== -1 && idx !== cur) {
                        cur = idx;
                        setActive(pins, idx);
                        count();
                        flyTo(idx);
                    }
                });
            }, { root: list, threshold: 0.5 });

            markerData.forEach((m) => {
                if (m.element) observer.observe(m.element);
            });

            markerData.forEach((m, i) => {
                if (m.element) {
                    m.element.addEventListener('click', (e) => {
                        if (e.target.closest('[data-globe-item-link]')) return;
                        go(i);
                    });
                }
            });

            pins.forEach((p, i) => {
                p.element.addEventListener('click', () => go(i));
            });

            list.setAttribute('tabindex', '0');
            list.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); go(cur + 1); }
                if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); go(cur - 1); }
            });

            count();
        }


        // Auto-rotate
        function initSpin(mapInstance) {
            let interacting = false;

            const spin = () => {
                const z = mapInstance.getZoom();
                if (cameraLocked || interacting || z >= cfg.maxSpinZoom) return;

                let speed = 360 / cfg.secondsPerRevolution;
                if (z > cfg.slowSpinZoom) {
                    speed *= (cfg.maxSpinZoom - z) / (cfg.maxSpinZoom - cfg.slowSpinZoom);
                }

                const c = mapInstance.getCenter();
                c.lng -= speed;

                mapInstance.easeTo({
                    center: c,
                    duration: 1000,
                    easing: (n) => n,
                });
            };

            mapInstance.on('mousedown', () => { interacting = true; });
            mapInstance.on('touchstart', () => { interacting = true; });

            ['mouseup', 'dragend', 'pitchend', 'rotateend', 'touchend'].forEach((e) =>
                mapInstance.on(e, () => { interacting = false; spin(); })
            );

            mapInstance.on('moveend', spin);
            resumeSpin = spin;
            spin();
        }


        // Panel toggle
        function initPanel(wrapperEl, mapInstance, getPad) {
            const info = wrapperEl.querySelector('[data-globe-info]');
            const close = wrapperEl.querySelector('[data-globe-close]');
            const open = wrapperEl.querySelector('[data-globe-reopen]');
            if (!info) return;

            const sync = () => {
                const collapsed = wrapperEl.getAttribute('data-collapsed') === 'true';
                if (!collapsed) {
                    return;
                }

                flyCamera({
                    center: cfg.center,
                    zoom: val('zoom'),
                    padding: getPad(0),
                });
            };

            new MutationObserver(sync).observe(wrapperEl, {
                attributes: true,
                attributeFilter: ['data-collapsed'],
            });

            if (close) close.addEventListener('click', () => wrapperEl.setAttribute('data-collapsed', 'true'));
            if (open) open.addEventListener('click', () => {
                wrapperEl.setAttribute('data-collapsed', 'false');
                const activePin = wrapperEl.querySelector('[data-globe-marker][data-active="true"]');
                if (activePin) {
                    activePin.click();
                }
            });
        }


        // Resize
        function initResize(mapInstance, wrapperEl, getPad) {
            const mql = window.matchMedia('(max-width: 991px)');
            const infoEl = wrapperEl.querySelector('[data-globe-info]');

            const apply = () => {
                mapInstance.resize();
                const collapsed = wrapperEl.getAttribute('data-collapsed') === 'true';
                const w = (!mql.matches && infoEl && !collapsed) ? infoEl.offsetWidth + 24 : 0;
                mapInstance.setPadding(getPad(w));
            };

            mql.addEventListener('change', apply);

            let t;
            window.addEventListener('resize', () => {
                clearTimeout(t);
                t = setTimeout(apply, 200);
            }, { passive: true });
        }


        // Helpers
        function txt(parent, sel) {
            const el = parent.querySelector(sel);
            return el ? el.textContent.trim() : '';
        }

        function attr(parent, sel, key) {
            const el = parent.querySelector(sel);
            return el ? el.getAttribute(key) : '';
        }
    }

    async function initGlobes(container) {
        const root = container || document;
        const wrappers = root.querySelectorAll(SELECTOR);

        if (!wrappers.length) {
            return;
        }

        const needsMapbox = Array.from(wrappers).some(
            (wrapper) => wrapper.getAttribute('data-mapbox-token')
        );
        if (!needsMapbox) {
            return;
        }

        try {
            const mapboxgl = await loadMapbox();
            wrappers.forEach((wrapper) => initInteractiveGlobeMapbox(wrapper, mapboxgl));
        } catch (error) {
            if (window.sojTheme?.debug) {
                // eslint-disable-next-line no-console
                console.warn('Map: failed to load Mapbox GL', error);
            }
        }
    }

    function onPageReady(e) {
        const container = e?.detail?.container || document;
        if (container.querySelector(SELECTOR)) {
            initGlobes(container);
        }
    }

    if (document.body?.hasAttribute?.('data-barba')) {
        document.addEventListener('barba:pageReady', onPageReady);
    } else if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () =>
            onPageReady({ detail: { container: document } })
        );
    } else {
        onPageReady({ detail: { container: document } });
    }
})();
